import { useState } from 'react';
import { CADENCES, type Cadence, type CreateRecurrenceBody } from './recurrence-api';
import { todayIso } from './occurrence-model';

/**
 * A new recurring duty, in the project the shell has selected.
 *
 * ⛔ NO PROJECT PICKER. `POST /projects/{id}/recurrences` takes the project in the PATH, and the
 * shell's rail already chooses it — a second control over one piece of state is how the rail ends up
 * saying FUND while the form creates in TBS, each looking correct on its own.
 *
 * ⚠️ THE BODY IS CAMELCASE — `intervalN`, `startsOn`, `endsOn`. The API's zod schema STRIPS unknown
 * keys, so `starts_on` would return 201 with the start date silently replaced by the schema's
 * requirement failing... in fact `startsOn` is REQUIRED, so a snake_case send is a 400 rather than a
 * quiet loss. `endsOn` is the one that would vanish quietly, which is why the whole body is spelled
 * out here rather than assembled from field names.
 *
 * ⚠️ `cadence` is a fixed set (a CHECK in db/002, and the README lists it among the values the
 * APPLICATION BRANCHES ON) — so it is a hard-coded list here, correctly. ⛔ The vocabularies are
 * fetched precisely because they are NOT this.
 *
 * ⭐ `startsOn` defaults to today rather than being left blank. A template with no start generates
 * nothing, and an empty required field is the commonest way to create one that silently does not run.
 */
export function TemplateForm({
  busy,
  error,
  onCreate,
}: {
  busy: boolean;
  error: string | null;
  onCreate: (body: CreateRecurrenceBody) => void;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [cadence, setCadence] = useState<Cadence>('weekly');
  const [intervalN, setIntervalN] = useState(1);
  const [assignee, setAssignee] = useState('');
  const [startsOn, setStartsOn] = useState(todayIso());
  const [endsOn, setEndsOn] = useState('');

  if (!open) {
    return (
      <div className="border-t border-[color:var(--color-line)] px-3 py-3 sm:px-4">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="h-9 rounded border border-dashed border-[color:var(--color-line)] px-3 text-sm font-semibold"
        >
          + New recurring duty
        </button>
      </div>
    );
  }

  const valid = title.trim().length > 0 && intervalN >= 1 && Boolean(startsOn);

  return (
    <form
      className="space-y-3 border-t border-[color:var(--color-line)] bg-[color:var(--color-surface-sunken)] px-3 py-3 sm:px-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        onCreate({
          title: title.trim(),
          description: description.trim() || null,
          cadence,
          intervalN,
          assignee: assignee.trim() || null,
          startsOn,
          // ⭐ Empty means UNBOUNDED — `null`, which db/002 documents as "runs until paused". An empty
          // string would be a date the API must reject.
          endsOn: endsOn || null,
        });
      }}
    >
      <h3 className="text-sm font-semibold">New recurring duty</h3>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold" htmlFor="rec-title">
            Title
          </label>
          <input
            id="rec-title"
            value={title}
            disabled={busy}
            onChange={(event) => setTitle(event.target.value)}
            className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-1.5 text-sm"
          />
          <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
            Becomes the title of every generated ticket.
          </p>
        </div>

        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold" htmlFor="rec-description">
            Description
          </label>
          <textarea
            id="rec-description"
            value={description}
            disabled={busy}
            rows={2}
            onChange={(event) => setDescription(event.target.value)}
            className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-1.5 text-sm"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold" htmlFor="rec-cadence">
            Cadence
          </label>
          <select
            id="rec-cadence"
            value={cadence}
            disabled={busy}
            onChange={(event) => setCadence(event.target.value as Cadence)}
            className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-2 text-sm"
          >
            {CADENCES.map((entry) => (
              <option key={entry} value={entry}>
                {entry}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
            A fixed set, not a vocabulary — the generator branches on it.
          </p>
        </div>

        <div>
          <label className="block text-xs font-semibold" htmlFor="rec-interval">
            Every N periods
          </label>
          <input
            id="rec-interval"
            type="number"
            min={1}
            max={365}
            value={intervalN}
            disabled={busy}
            onChange={(event) => setIntervalN(Math.max(1, Number(event.target.value) || 1))}
            className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-1.5 text-sm"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold" htmlFor="rec-assignee">
            Owner
          </label>
          <input
            id="rec-assignee"
            value={assignee}
            disabled={busy}
            onChange={(event) => setAssignee(event.target.value)}
            className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-1.5 text-sm"
          />
          <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
            Becomes the assignee of every generated ticket.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-semibold" htmlFor="rec-starts">
              Starts
            </label>
            <input
              id="rec-starts"
              type="date"
              value={startsOn}
              disabled={busy}
              onChange={(event) => setStartsOn(event.target.value)}
              className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold" htmlFor="rec-ends">
              Ends
            </label>
            <input
              id="rec-ends"
              type="date"
              value={endsOn}
              disabled={busy}
              onChange={(event) => setEndsOn(event.target.value)}
              className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-1.5 text-sm"
            />
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">Blank = until paused.</p>
          </div>
        </div>
      </div>

      {error ? (
        <p role="alert" className="text-xs font-semibold text-red-700 dark:text-red-300">
          {error}
        </p>
      ) : null}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy || !valid}
          className="h-9 rounded bg-[color:var(--color-accent)] px-3 text-sm font-semibold text-[color:var(--color-on-accent)] disabled:opacity-50"
        >
          Create template
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="h-9 rounded border border-[color:var(--color-line)] px-3 text-sm"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
